import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';
import { ReasoningStepsProps, ToolInChecklist } from '@/types/chat';

export const ReasoningSteps = ({
  steps,
  isComplete,
  streamingStatus,
  isRestored,
  restoredDurationSec,
}: ReasoningStepsProps) => {
  const [isThinkingExpanded, setIsThinkingExpanded] = useState(true);
  const [hasAutoCollapsed, setHasAutoCollapsed] = useState(false);
  const [thinkingStartTime] = useState(() => Date.now());
  const [totalDuration, setTotalDuration] = useState<number | null>(null);
  const reasoningTimestampsRef = useRef<Map<number, number>>(new Map());
  const completionTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (isComplete && !hasAutoCollapsed) {
      setIsThinkingExpanded(false);
      setHasAutoCollapsed(true);
    }
  }, [isComplete, hasAutoCollapsed]);

  useEffect(() => {
    if (isComplete && totalDuration === null && !isRestored) {
      const elapsed = Math.round((Date.now() - thinkingStartTime) / 1000);
      setTotalDuration(elapsed);
      completionTimeRef.current = Date.now();
    }
  }, [isComplete, totalDuration, thinkingStartTime, isRestored]);

  const allReasoning = useMemo(() => {
    return (
      steps
        ?.filter(
          (step): step is typeof step & { content: string } => !!step.content,
        )
        .map(step => step.content) || []
    );
  }, [steps]);

  const allTools = useMemo(() => {
    return steps?.flatMap(step => step.tools || []) || [];
  }, [steps]);

  useEffect(() => {
    allReasoning.forEach((_, i) => {
      if (!reasoningTimestampsRef.current.has(i)) {
        reasoningTimestampsRef.current.set(i, Date.now());
      }
    });
  }, [allReasoning.length]);

  const isStreaming = !isComplete && streamingStatus;

  if ((!steps || steps.length === 0) && !isStreaming) return null;

  const hasReasoning = allReasoning.length > 0;
  const hasTools = allTools.length > 0;
  const effectiveDuration = totalDuration ?? restoredDurationSec ?? null;
  const durationLabel =
    effectiveDuration !== null ? `${effectiveDuration}s` : null;

  let thinkingHeaderLabel: string;
  if (isStreaming) {
    thinkingHeaderLabel =
      streamingStatus === 'Running tools...'
        ? 'Running tools'
        : streamingStatus === 'Generating response...'
        ? 'Generating response'
        : 'Thinking';
  } else if (allReasoning.length <= 1) {
    thinkingHeaderLabel = durationLabel
      ? `Thought for ${durationLabel}`
      : 'Thought for a moment';
  } else {
    thinkingHeaderLabel = durationLabel
      ? `${allReasoning.length} steps · ${durationLabel}`
      : `${allReasoning.length} steps`;
  }

  const getReasoningDuration = (reasoningIndex: number): number | null => {
    if (isRestored) {
      if (restoredDurationSec && allReasoning.length > 0) {
        const base = Math.floor(restoredDurationSec / allReasoning.length);
        const remainder = restoredDurationSec - base * allReasoning.length;
        return Math.max(1, reasoningIndex === 0 ? base + remainder : base);
      }
      return null;
    }
    const startTime = reasoningTimestampsRef.current.get(reasoningIndex);
    if (!startTime) return null;
    const nextTime =
      reasoningTimestampsRef.current.get(reasoningIndex + 1) ??
      completionTimeRef.current;
    if (!nextTime) return null;
    return Math.max(1, Math.round((nextTime - startTime) / 1000));
  };

  return (
    <div className="w-full mb-3 pb-3 border-b border-slate-300">
      {/* Thinking / Reasoning section */}
      <button
        onClick={() => setIsThinkingExpanded(!isThinkingExpanded)}
        className="flex items-center gap-1.5 py-1.5 text-left group">
        {isThinkingExpanded ? (
          <ChevronDown className="h-4 w-4 text-[#768D95]" />
        ) : (
          <ChevronRight className="h-4 w-4 text-[#768D95]" />
        )}
        <span className="text-sm text-[#768D95] group-hover:text-[#252F33] transition-colors">
          {thinkingHeaderLabel}
          {isStreaming && <BouncingDots />}
        </span>
      </button>

      {isThinkingExpanded && (
        <>
          {hasReasoning && (
            <div className="mt-1 pl-1">
              {allReasoning.length === 1 ? (
                <div className="ml-3 pl-3 border-l-2 border-slate-200 py-1">
                  <ReasoningContent
                    content={allReasoning[0]}
                    isRestored={!!isRestored}
                    isComplete={!!isComplete}
                  />
                </div>
              ) : (
                <div className="space-y-0.5">
                  {allReasoning.map((content, i) => (
                    <ReasoningListItem
                      key={`r-${i}`}
                      content={content}
                      isStreamingStep={
                        !!(isStreaming && i === allReasoning.length - 1)
                      }
                      isRestored={!!isRestored}
                      isComplete={!!isComplete}
                      duration={getReasoningDuration(i)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {hasTools && (
            <div className="mt-2 max-h-[120px] overflow-y-auto space-y-1">
              {allTools.map(tool => (
                <ToolItem key={tool.callId} tool={tool} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ReasoningContent = ({
  content,
  isRestored,
  isComplete,
}: {
  content: string;
  isRestored: boolean;
  isComplete?: boolean;
}) => {
  const showInstantly = isRestored || !!isComplete;
  const [displayedContent, setDisplayedContent] = useState(
    showInstantly ? content : '',
  );
  const [hasFinishedStreaming, setHasFinishedStreaming] =
    useState(showInstantly);

  useEffect(() => {
    if (!content) return;

    if (showInstantly) {
      setDisplayedContent(content);
      setHasFinishedStreaming(true);
      return;
    }

    if (hasFinishedStreaming) {
      setDisplayedContent(content);
      return;
    }

    const words = content.split(' ');
    let currentWordIndex = 0;
    setDisplayedContent('');
    setHasFinishedStreaming(false);

    const interval = setInterval(() => {
      if (currentWordIndex < words.length) {
        setDisplayedContent(words.slice(0, currentWordIndex + 1).join(' '));
        currentWordIndex++;
      } else {
        setHasFinishedStreaming(true);
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [content, showInstantly]);

  const isTextStreaming =
    !hasFinishedStreaming && displayedContent.length < content.length;

  return (
    <div className="text-sm text-[#4A5568] leading-relaxed whitespace-pre-wrap">
      {displayedContent}
      {isTextStreaming && (
        <span className="inline-block w-1 h-4 ml-1 bg-[#007CA0] animate-pulse" />
      )}
    </div>
  );
};

const ReasoningListItem = ({
  content,
  isStreamingStep,
  isRestored,
  isComplete,
  duration,
}: {
  content: string;
  isStreamingStep: boolean;
  isRestored: boolean;
  isComplete: boolean;
  duration: number | null;
}) => {
  const [isExpanded, setIsExpanded] = useState(!isComplete);
  const [hasAutoCollapsed, setHasAutoCollapsed] = useState(false);

  useEffect(() => {
    if (isComplete && !hasAutoCollapsed && !isStreamingStep) {
      setIsExpanded(false);
      setHasAutoCollapsed(true);
    }
  }, [isComplete, hasAutoCollapsed, isStreamingStep]);

  const title = isStreamingStep
    ? 'Thinking'
    : duration
    ? `Thought for ${duration}s`
    : 'Thought for a moment';

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 py-1 text-left w-full">
        <span className="w-1.5 h-1.5 rounded-full bg-[#A0AEB4] flex-shrink-0" />
        <span className="text-sm text-[#768D95]">{title}</span>
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-[#A0AEB4]" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-[#A0AEB4]" />
        )}
      </button>
      {isExpanded && (
        <div className="ml-4 pl-3 border-l-2 border-slate-200 py-1 mb-1">
          <ReasoningContent
            content={content}
            isRestored={isRestored}
            isComplete={isComplete}
          />
        </div>
      )}
    </div>
  );
};

const DOT_BOUNCE_STYLE = `@keyframes dot-bounce{0%,60%,100%{transform:translateY(0);opacity:.4}30%{transform:translateY(-3px);opacity:1}}`;
let styleInjected = false;

const BouncingDots = () => {
  if (!styleInjected && typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = DOT_BOUNCE_STYLE;
    document.head.appendChild(style);
    styleInjected = true;
  }

  return (
    <span className="inline-flex ml-0.5 gap-[2px]" aria-hidden>
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className="inline-block w-[3px] h-[3px] rounded-full bg-current"
          style={{
            animation: 'dot-bounce 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </span>
  );
};

const ToolItem = ({ tool }: { tool: ToolInChecklist }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isPending = tool.status === 'pending';
  const isFailed = tool.status === 'error';
  const hasDescription = !!tool.description;

  return (
    <div>
      <button
        type="button"
        onClick={() => hasDescription && setIsOpen(!isOpen)}
        className={`flex items-center gap-2 py-1 pl-1 w-full text-left ${
          hasDescription ? 'cursor-pointer' : 'cursor-default'
        }`}>
        {isPending ? (
          <Loader2 className="h-4 w-4 text-[#007CA0] animate-spin flex-shrink-0" />
        ) : isFailed ? (
          <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
        ) : (
          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
        )}
        <span className="text-sm text-[#768D95]">{tool.name}</span>
        {hasDescription &&
          (isOpen ? (
            <ChevronDown className="h-3.5 w-3.5 text-[#A0AEB4] flex-shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-[#A0AEB4] flex-shrink-0" />
          ))}
      </button>
      {isOpen && tool.description && (
        <div className="ml-7 pl-3 border-l-2 border-slate-200 py-1 mb-1">
          <p className="text-xs text-[#768D95] leading-relaxed whitespace-pre-wrap">
            {tool.description}
          </p>
        </div>
      )}
    </div>
  );
};
